import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Divider, Flex } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import theme from '../assets/theme';
import CustomImage from '../components/CustomImage';
import './Layout.scss';
import logo from '../assets/images/logo1.png';
import SettingsIcon from '../assets/images/settings-icon.svg';
import ShipmentIcon from '../assets/images/shipment-icon.svg';
import AiHistoryIcon from '../assets/images/ai-history-icon.svg';
import InventoryIcon from '../assets/images/inventory-menu-icon.svg';
import FlyingIcon from '../assets/images/flying-icon.svg';
import {
  GearIcon,
  FlyIcon,
  VideoSquareIcon,
  HomeIcon,
  LogiblocksIcon,
  AIHistoryPageIcon,
  ListMenuIcon,
  InventoryPageColorIcon,
} from '@/assets/icons/icons';
import { useToken } from '../common/helper';
import { lanePlannerEnabledForCompany } from '../common/constants';
import ChatIcon from '../assets/images/ChatIcon';
import { useSelector } from 'react-redux';
import {
  MenuItemData,
  FeaturePermissionMatrix,
  SubMenuData,
} from '@/apis/types';
import { PermissionsObject } from '@/pages/Data/types';
import DownloadReportModal from '@/pages/Data/DownloadReportModal';
import { DashboardIcon1 as DashboardIcon } from '@/assets/images';

interface RootState {
  menu: {
    items: MenuItemData[];
  };
}

type IconMapValue =
  | string
  | React.ComponentType<{ color?: string; size?: number }>
  | React.ReactElement;

/** Globe icon for the Lane planner module (no globe in the shared icon set). */
const LanePlannerGlobeIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <circle cx='12' cy='12' r='9' stroke={color} strokeWidth='1.6' />
    <ellipse cx='12' cy='12' rx='4' ry='9' stroke={color} strokeWidth='1.6' />
    <path d='M3.5 9h17M3.5 15h17' stroke={color} strokeWidth='1.6' />
  </svg>
);

interface CustomSidebarProps {
  // Data modal state, owned by DashboardLayout so the Help center can see it.
  reportModalOpen?: boolean;
  setReportModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomSidebar: React.FC<CustomSidebarProps> = (props) => {
  // Falls back to local state when rendered without the layout (e.g. tests).
  const [localReportModalOpen, setLocalReportModalOpen] = useState(false);
  const reportModalOpen = props.reportModalOpen ?? localReportModalOpen;
  const setReportModalOpen =
    props.setReportModalOpen ?? setLocalReportModalOpen;
  const navigate = useNavigate();
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { t } = useTranslation('menu');
  const activeIconColor = theme.activeIcon || theme.primary;

  // Function to get translated menu item label
  const getMenuItemLabel = (item: MenuItemData): string => {
    // Map menu item labels to translation keys
    const labelKey = item.label.toLowerCase().replace(/\s+/g, '');

    // Special cases
    if (labelKey === 'automations') {
      return 'Automations';
    }
    if (labelKey === 'shipment') {
      return 'Shipments';
    }
    // Try to get translation, fallback to original label
    const translationKey = labelKey as keyof typeof t;
    const translated = t(translationKey, { defaultValue: item.name });

    return typeof translated === 'string' ? translated : item.name;
  };
  // Chatbot icons with JSX, assigned to variables
  const chatIcon = <ChatIcon fillcolor='#3D81FF' />;
  const coloredChatIcon = <ChatIcon color={theme.primary} fillcolor='white' />;
  const LogiblocksIconDefault = <LogiblocksIcon color={'white'} size={20} />;
  const LogiblocksIconColored = (
    <LogiblocksIcon color={activeIconColor} size={20} />
  );
  const DataIconDefault = <ListMenuIcon fill={'white'} size={20} />;
  const DataIconColored = <ListMenuIcon fill={activeIconColor} size={20} />;

  const iconMap: Record<string, IconMapValue> = {
    // base icons (non-colored)
    DashboardIcon,
    SettingsIcon,
    ShipmentIcon,
    FlyingIcon,
    AiHistoryIcon,
    InventoryIcon,

    // theme-specific (colored) icons
    ColoredDashboardIcon: HomeIcon,
    ColoredSettingsIcon: GearIcon,
    ColoredFlyingIcon: FlyIcon,
    ColoredShipmentIcon: VideoSquareIcon,
    ColoredAIHistoryIcon: AIHistoryPageIcon,
    ColoredInventoryIcon: InventoryPageColorIcon,

    // Chat icons
    ChatIcon: chatIcon,
    ColoredChatIcon: coloredChatIcon,

    // Logiblocks icons
    LogiBlocksIcon: LogiblocksIconDefault, // non-active state
    ColoredLogiBlocksIcon: LogiblocksIconColored, // active state

    //Data icon
    DataIcon: DataIconDefault,
    ColoredDataIcon: DataIconColored,

    // Lane planner icons (FS2 pilot module)
    LanePlannerIcon: <LanePlannerGlobeIcon color='white' />,
    ColoredLanePlannerIcon: <LanePlannerGlobeIcon color={activeIconColor} />,
  };
  // menu from redux/store
  const menuItems = useSelector((state: RootState) => state.menu.items);
  const visibleMenuItems = menuItems.filter((item) => item.name !== 'Chatbot'); // to remove later

  // Lane planner (cargo.one) pilot: injected client-side for the allowlisted
  // company instead of a Features DB row — promote to a real backend menu
  // feature when the pilot goes GA. Placed right after Shipments.
  const companyId = decodedToken?.company_id;
  const lanePlannerItem: MenuItemData | null = useMemo(
    () =>
      lanePlannerEnabledForCompany(companyId)
        ? {
            id: 'lane-planner',
            name: 'Lane planner',
            label: 'Lane planner',
            icon: 'LanePlannerIcon',
            coloredIcon: 'ColoredLanePlannerIcon',
            route: 'lane-planner',
            permissions: {
              is_create: true,
              is_update: true,
              is_delete: false,
              is_view: true,
            },
            sub_menus: [] as SubMenuData[],
          }
        : null,
    [companyId]
  );
  if (
    lanePlannerItem &&
    !visibleMenuItems.some((item) => item.route === 'lane-planner')
  ) {
    // Backend Features route for Shipments is "shipment" (singular).
    const shipmentsIndex = visibleMenuItems.findIndex(
      (item) => item.route === 'shipment'
    );
    visibleMenuItems.splice(
      shipmentsIndex === -1 ? visibleMenuItems.length : shipmentsIndex + 1,
      0,
      lanePlannerItem
    );
  }
  const downloadDataPermissions: FeaturePermissionMatrix | undefined =
    visibleMenuItems?.find(
      (x) => x.name?.toLowerCase() === 'data'
    )?.permissions;

  // Convert FeaturePermissionMatrix to PermissionsObject with default values
  const permissionsForModal: PermissionsObject = {
    is_create: downloadDataPermissions?.is_create ?? false,
    is_update: downloadDataPermissions?.is_update ?? false,
    is_delete: downloadDataPermissions?.is_delete ?? false,
    is_view: downloadDataPermissions?.is_view ?? false,
    create_self: downloadDataPermissions?.create_self ?? false,
    edit_contacts_only: downloadDataPermissions?.edit_contacts_only ?? false,
  };
  const filteredMenuItems = visibleMenuItems.filter((item) => {
    const userRole = decodedToken?.role?.toLowerCase();
    const isAdminOrEditor = userRole === 'admin' || userRole === 'editor';
    const isChatbotRestricted =
      item.route === 'chatbot' &&
      decodedToken?.company_name?.toLowerCase() !== 'zoomlogi';

    return (
      isAdminOrEditor || item.route === 'airport-list' || !isChatbotRestricted
    );
  });

  // Data should always sit immediately before Settings.
  const orderedMenuItems = (() => {
    const dataIndex = filteredMenuItems.findIndex(
      (item) => item.label?.toLowerCase() === 'data'
    );
    const settingsIndex = filteredMenuItems.findIndex(
      (item) => item.label === 'settings'
    );

    if (dataIndex === -1 || settingsIndex === -1 || dataIndex < settingsIndex) {
      return filteredMenuItems;
    }

    const reordered = [...filteredMenuItems];
    const [dataItem] = reordered.splice(dataIndex, 1);
    reordered.splice(settingsIndex, 0, dataItem);
    return reordered;
  })();

  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState<string>('dashboard');
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  useEffect(() => {
    const currentPath = location.pathname.split('/')[1];
    // Pilot Lane planner is injected into the visible menu but not Redux
    // menuItems — check both so /lane-planner highlights correctly.
    const activeItem =
      menuItems.find((item) => item.route === currentPath) ||
      (lanePlannerItem?.route === currentPath ? lanePlannerItem : null);
    setActiveMenu(activeItem?.route ?? 'dashboard');
  }, [location, menuItems, lanePlannerItem]);

  const handleSetActiveMenu = (menuItem: MenuItemData): void => {
    if (menuItem.route) {
      setActiveMenu(menuItem.route || '');
    }

    // Check if the item is not a route, if so open report modal instead of navigating
    if (menuItem.is_route === false) {
      setReportModalOpen(true);
    } else {
      const _ = navigate(`/${menuItem.route}`);
    }
  };

  const getIcon = (
    menuItem: string | undefined,
    defaultIcon: string | undefined,
    coloredIcon: string | undefined
  ): IconMapValue => {
    const iconName =
      hoveredMenu === menuItem || activeMenu === menuItem
        ? coloredIcon
        : defaultIcon;
    return (iconName && iconMap[iconName]) || DashboardIcon; // fallback to DashboardIcon if not found
  };

  return (
    <>
      <div className='sidebar-content sidebar-content--rebrand'>
        <div className='logo'>
          <Flex justify='center'>
            <CustomImage
              src={logo}
              preview={false}
              className='header-zoomlogi-logo is-sidebar-logo'
            />
          </Flex>
        </div>

        <ul className='menu'>
          {(() => {
            const firstDividerItem = orderedMenuItems.find(
              (menuItem) =>
                menuItem.route === 'settings' ||
                menuItem.name?.toLowerCase() === 'data'
            );

            return orderedMenuItems.map((item) => {
              const shouldShowDivider = item === firstDividerItem;
              return (
                <React.Fragment key={item.route}>
                  {shouldShowDivider && (
                    <div className='sidebar-divider-wrap'>
                      <Divider className='notification-divider' />
                    </div>
                  )}
                  <li
                    key={item.route}
                    className={`sidebar-menu-item${
                      activeMenu === item.route ? ' active' : ''
                    }`}
                    onClick={() => handleSetActiveMenu(item)}
                    onMouseEnter={() => setHoveredMenu(item.route ?? null)}
                    onMouseLeave={() => setHoveredMenu(null)}
                  >
                    <Flex
                      vertical={true}
                      justify='center'
                      align='center'
                      gap={2}
                      style={{ color: '#ffffffcc' }}
                    >
                      <Flex
                        className='menu-item-icon'
                        align='center'
                        justify='center'
                        style={
                          {
                            '--active-route-bg': theme.activeRouteBg,
                          } as React.CSSProperties
                        }
                      >
                        {(() => {
                          const icon = getIcon(
                            item.route,
                            item.icon,
                            item.coloredIcon
                          );
                          return (
                            <Avatar
                              size={21}
                              src={typeof icon === 'string' ? icon : undefined}
                              icon={
                                typeof icon !== 'string' &&
                                typeof icon === 'function'
                                  ? React.createElement(icon, {
                                      color: activeIconColor,
                                      size: 20,
                                    })
                                  : typeof icon !== 'string' &&
                                      React.isValidElement(icon)
                                    ? icon
                                    : undefined
                              }
                              style={{
                                backgroundColor:
                                  hoveredMenu === 'chatbot' ||
                                  activeMenu === 'chatbot'
                                    ? 'white'
                                    : 'transparent',
                              }}
                            />
                          );
                        })()}
                      </Flex>
                      <span className='sidebar-menu-label'>
                        {getMenuItemLabel(item)}
                      </span>
                    </Flex>
                  </li>
                </React.Fragment>
              );
            });
          })()}
        </ul>
      </div>
      {reportModalOpen && (
        <DownloadReportModal
          reportModalOpen={reportModalOpen}
          setReportModalOpen={setReportModalOpen}
          onBack={() => {
            setReportModalOpen(false);
          }}
          title={'Data'}
          showBackIcon={false}
          permissions={permissionsForModal}
        />
      )}
    </>
  );
};

export default CustomSidebar;
